'use client';

import { cn } from '@@/lib/utils';

import {
  Button,
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@@/ui/primitives';
import { Check, ChevronsUpDown, CircleAlertIcon, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useSuggestions } from '../../../hooks/use-suggestions';

export function SearchConnectResourceInput(props: {
  type: 'USER' | 'QUEUE';
  disabled: boolean;
  selected: string[];
  onSelect: (value: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [searchPrefix, setSearch] = useState('');
  const { data, isLoading, error } = useSuggestions({
    type: props.type,
    prefix: searchPrefix
  });

  const selectedCount = props.selected.length;
  const buttonText = selectedCount === 0 ? 'Search...' : `${selectedCount} selected`;

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <Button
          aria-expanded={open}
          className='justify-between text-sm!'
          role='combobox'
          variant='outline'
          disabled={props.disabled}
        >
          {buttonText}
          <ChevronsUpDown className='ml-2 size-4 shrink-0 opacity-50' />
        </Button>
      </PopoverTrigger>
      <PopoverContent className='p-0 font-sans' align='start'>
        <Command shouldFilter={false}>
          <CommandInput
            onValueChange={setSearch}
            className='text-sm!'
            placeholder='Type to search...'
            value={searchPrefix}
          />
          <CommandList>
            {error && (
              <div className='flex items-center p-2 px-3 text-sm text-amber-800'>
                <CircleAlertIcon className='size-4 inline-block mr-2' />
                Unable to load Suggestions
              </div>
            )}
            {isLoading ? (
              <div className='flex items-center justify-center p-4'>
                <Loader2 className='size-4 animate-spin' />
                <span className='ml-2 text-muted-foreground text-sm'>Searching...</span>
              </div>
            ) : (
              <>
                {data && data.results && data.results.length > 0 && (
                  <CommandGroup>
                    {data.results.map((result) => {
                      const isSelected = props.selected.includes(result.id);
                      return (
                        <CommandItem
                          key={result.id}
                          onSelect={() => {
                            const newSelected = isSelected
                              ? props.selected.filter((id) => id !== result.id)
                              : [...props.selected, result.id];
                            props.onSelect(newSelected);
                          }}
                          value={result.name}
                        >
                          <div className='flex flex-col w-full'>
                            <span className='font-medium'>{result.name}</span>
                            <span className='text-xs text-neutral-500 font-mono truncate max-w-53'>{result.id}</span>
                          </div>

                          <Check className={cn('mr-2 size-4', isSelected ? 'opacity-100' : 'opacity-0')} />
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                )}
              </>
            )}
            {error && searchPrefix && (
              <CommandGroup>
                <CommandItem
                  onSelect={() => {
                    const isSelected = props.selected.includes(searchPrefix);
                    const newSelected = isSelected
                      ? props.selected.filter((id) => id !== searchPrefix)
                      : [...props.selected, searchPrefix];
                    props.onSelect(newSelected);
                    setSearch('');
                  }}
                  value={searchPrefix}
                >
                  <div className='flex flex-col w-full'>
                    <span className='font-medium'>"{searchPrefix}"</span>
                  </div>
                  <Check
                    className={cn('mr-2 size-4', props.selected.includes(searchPrefix) ? 'opacity-100' : 'opacity-0')}
                  />
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
